import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send } from "lucide-react";
import clsx from "clsx";
import { btnSlimCls, btnSlimPrimaryCls } from "components/tw/classes";
import SendInvoiceDialog, { type SendableInvoice } from "./send-invoice-dialog";
import type { InvoiceSendStatus } from "types/Invoice";

type Props = {
  invoice: SendableInvoice;
  /** Called with the new send status after the email was attempted. */
  onSent: (send: InvoiceSendStatus) => void;
  /** Extra classes for the button, e.g. to widen it in a narrow card. */
  className?: string;
};

/** "Send" for an invoice nobody was emailed yet, "Send again" otherwise; opens the recipient dialog. */
export default function SendInvoiceButton({ invoice, onSent, className }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const neverSent = invoice.send.status === "not_sent";

  return (
    <>
      <button
        type="button"
        className={clsx(neverSent ? btnSlimPrimaryCls : btnSlimCls, className)}
        onClick={() => setOpen(true)}
      >
        <Send className="inline size-3.5" aria-hidden="true" />
        {neverSent
          ? t("send", { defaultValue: "Send" })
          : t("invoice_send_again", { defaultValue: "Send again" })}
      </button>
      {open && (
        <SendInvoiceDialog
          invoice={invoice}
          onClose={() => setOpen(false)}
          onSent={(send) => {
            setOpen(false);
            onSent(send);
          }}
        />
      )}
    </>
  );
}
